// src/features/announcements/announcementThunks.js
import { createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../../api/axiosInstance';

/**
 * Fetch all announcements with optional filters
 */
export const fetchAnnouncementsThunk = createAsyncThunk(
  'announcements/fetchAll',
  async (filters = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      
      if (filters.store) params.append('store', filters.store);
      if (filters.announced_by) params.append('announced_by', filters.announced_by);
      if (filters.date) params.append('date', filters.date);
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      
      // Backend now paginates the raw list (limit/offset, max 100) - it
      // previously returned the ENTIRE announcements table. Unwrap the
      // envelope so existing consumers keep receiving a plain array.
      if (!params.has('limit')) params.append('limit', '100');
      const response = await axiosInstance.get(`/api/announcements/?${params.toString()}`);
      const d = response.data;
      return Array.isArray(d) ? d : (d.results ?? []);
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch announcements');
    }
  }
);

/**
 * Fetch a single announcement by ID
 */
export const fetchAnnouncementThunk = createAsyncThunk(
  'announcements/fetchOne',
  async (announcementId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`/api/announcements/${announcementId}/`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch announcement');
    }
  }
);

/**
 * Create a new announcement
 */
export const createAnnouncementThunk = createAsyncThunk(
  'announcements/create',
  async (announcementData, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post('/api/announcements/', announcementData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to create announcement');
    }
  }
);

/**
 * Update an existing announcement
 */
export const updateAnnouncementThunk = createAsyncThunk(
  'announcements/update',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.patch(`/api/announcements/${id}/`, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to update announcement');
    }
  }
);

/**
 * Delete an announcement
 */
export const deleteAnnouncementThunk = createAsyncThunk(
  'announcements/delete',
  async (announcementId, { rejectWithValue }) => {
    try {
      await axiosInstance.delete(`/api/announcements/${announcementId}/`);
      return announcementId;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to delete announcement');
    }
  }
);

/**
 * Fetch announcements grouped by store
 */
export const fetchAnnouncementsByStoreThunk = createAsyncThunk(
  'announcements/fetchByStore',
  async (filters = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      
      if (filters.store) params.append('store', filters.store);
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      
      // Backend now paginates announcements under this endpoint (max 100).
      if (!params.has('limit')) params.append('limit', '100');
      const response = await axiosInstance.get(`/api/announcements/by-store/?${params.toString()}`);
      const d = response.data;
      return Array.isArray(d) ? d : (d.results ?? []);
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch announcements by store');
    }
  }
);

/**
 * Fetch announcements in table view format
 */
export const fetchAnnouncementsTableViewThunk = createAsyncThunk(
  'announcements/fetchTableView',
  async (filters = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      
      if (filters.store) params.append('store', filters.store);
      if (filters.announced_by) params.append('announced_by', filters.announced_by);
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      
      // Backend now paginates this endpoint (limit/offset, max 100).
      // Request a full page and unwrap the envelope so existing
      // components keep receiving a plain array.
      if (!params.has('limit')) params.append('limit', '100');
      const response = await axiosInstance.get(`/api/announcements/table-view/?${params.toString()}`);
      const d = response.data;
      return Array.isArray(d) ? d : (d.results ?? []);
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch table view');
    }
  }
);

/**
 * Fetch today's announcements
 */
export const fetchTodayAnnouncementsThunk = createAsyncThunk(
  'announcements/fetchToday',
  async (storeId = null, { rejectWithValue }) => {
    try {
      const params = storeId ? `?store=${storeId}` : '';
      const response = await axiosInstance.get(`/api/announcements/today/${params}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch today\'s announcements');
    }
  }
);

/**
 * Fetch announcements grouped by employee
 */
export const fetchAnnouncementsByEmployeeThunk = createAsyncThunk(
  'announcements/fetchByEmployee',
  async (filters = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      
      if (filters.store) params.append('store', filters.store);
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      
      const response = await axiosInstance.get(`/api/announcements/by-employee/?${params.toString()}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch announcements by employee');
    }
  }
);

/**
 * Fetch announcement statistics
 */
export const fetchAnnouncementStatsThunk = createAsyncThunk(
  'announcements/fetchStats',
  async (filters = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      
      if (filters.store) params.append('store', filters.store);
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      
      const response = await axiosInstance.get(`/api/announcements/stats/?${params.toString()}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch announcement statistics');
    }
  }
);

/**
 * Check if user can edit an announcement
 */
export const checkCanEditThunk = createAsyncThunk(
  'announcements/checkCanEdit',
  async (announcementId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`/api/announcements/${announcementId}/can-edit/`);
      return { announcementId, ...response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to check edit permission');
    }
  }
);